import * as React from 'react';

type AtomFamilyWithRemove<T> = {
  (param: T): unknown;
  remove: (param: T) => void;
};

type CleanupOptions = {
  cleanupDelay?: number;
};

const DEFAULT_CLEANUP_DELAY = 60000;

// Module-level map to track pending cleanups by serialized param
const pendingCleanups = new Map<string, ReturnType<typeof setTimeout>>();

function serializeParam<T>(param: T): string {
  return JSON.stringify(param);
}

export function useAtomFamilyCleanup<T>(
  atomFamilies: AtomFamilyWithRemove<T>[],
  param: T,
  options?: CleanupOptions,
) {
  const delay = options?.cleanupDelay ?? DEFAULT_CLEANUP_DELAY;
  const paramKey = serializeParam(param);

  // Use ref to always have latest atomFamilies without re-running effect
  const atomFamiliesRef = React.useRef(atomFamilies);
  atomFamiliesRef.current = atomFamilies;

  React.useEffect(() => {
    // On mount: cancel any pending cleanup for this param
    const pendingTimeout = pendingCleanups.get(paramKey);
    if (pendingTimeout) {
      clearTimeout(pendingTimeout);
      pendingCleanups.delete(paramKey);
    }

    // On unmount: schedule cleanup
    return () => {
      const currentAtomFamilies = atomFamiliesRef.current;

      const timeoutId = setTimeout(() => {
        currentAtomFamilies.forEach((family) => family.remove(param));
        pendingCleanups.delete(paramKey);
      }, delay);

      pendingCleanups.set(paramKey, timeoutId);
    };
  }, [paramKey, delay, param]);
}
